/**
 * Who gets the unified dashboard (#1337, owner decision 06.10): with the
 * closed preview on for the selected organization, the dashboard `/dashboard`
 * would draw for a client, an admin or a supervisor — a grant holder included,
 * whose dashboard `dashboardFor` already picked — is replaced by the shell;
 * the agent's, by the shell's personal view, «Моё» (#1338 PR C). The platform
 * owner's own dashboard is decided before this is asked. Off — the role
 * dashboards, untouched.
 *
 * A module of its own, with nothing of the shell's layout in it: `/dashboard`
 * asks this for every client, and the preview is closed for nearly all of them
 * — the layout table and the window helpers must not ride in its chunk
 * (`unified-gate.ts` holds what needs them).
 */
import { UNIFIED_DASHBOARD_FLAG_KEY } from '@aica/core';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import type { GlobalRole } from '@/lib/route-permissions';
import type { UnifiedView } from './unified-search';

/**
 * The preview by the flag map as it stands now — the localStorage seed or the
 * network's answer, in the portal; the session's subset in Bitrix24 (#1347).
 * Nothing waits for `/feature-flags`: the preview is closed for nearly
 * everyone, and a skeleton in front of every client's dashboard would be paid
 * for by all of them. No answer (no organisation, a failed read) is `off`: the
 * role dashboards stay.
 */
export function useUnifiedDashboardEnabled(): boolean {
  const { isEnabled } = useFeatureFlagsSafe();
  return isEnabled(UNIFIED_DASHBOARD_FLAG_KEY);
}

const SHELL_DASHBOARDS: ReadonlySet<GlobalRole> = new Set([
  'client',
  'admin',
  'supervisor',
]);

/**
 * Which view of the shell replaces this role dashboard (`dashboardFor`'s
 * answer): the business tabs, the employee's own («Моё») for the agent's
 * dashboard, or none — the role dashboard stays.
 */
export function unifiedViewOf(
  dashboard: GlobalRole | null,
  enabled: boolean
): UnifiedView | null {
  if (!enabled || dashboard === null) return null;
  if (dashboard === 'agent') return 'personal';
  return SHELL_DASHBOARDS.has(dashboard) ? 'business' : null;
}
